import { CSSProperties } from '@linaria/core';
import { styled } from '@linaria/react';
import { Button as ReakitButton } from 'reakit';
import { baseTheme } from 'styles/utils';
export type VariantTypes = 'primary' | 'secondary' | 'tertiary' | 'link' | 'danger' | 'unstyled';
export type ButtonStateTypes = 'normal' | 'hover' | 'active' | 'disable';
export type ButtonStateStyles = Record<ButtonStateTypes, CSSProperties>;
export type ButtonStatesCustomStyles = Record<VariantTypes, ButtonStateStyles>;
export const boxShadowColor = `#1018280D`;
export const defaultButtonVariantsStyles: ButtonStatesCustomStyles = {
  primary: {
    normal: {
      color: baseTheme.light.colors.white,
      backgroundColor: baseTheme.light.colors.primary600,
      border: `1px solid ${baseTheme.light.colors.primary600}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`
    },
    hover: {
      color: baseTheme.light.colors.white,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      backgroundColor: baseTheme.light.colors.primary700,
      border: `1px solid ${baseTheme.light.colors.primary700}`
    },
    active: {
      color: baseTheme.light.colors.white,
      backgroundColor: baseTheme.light.colors.primary600,
      border: `1px solid ${baseTheme.light.colors.primary}`,
      boxShadow: ` 0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${baseTheme.light.colors.primary100}`
    },
    disable: {
      color: baseTheme.light.colors.white,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      backgroundColor: baseTheme.light.colors.primary200,
      border: `1px solid ${baseTheme.light.colors.primary200}`
    }
  },
  secondary: {
    normal: {
      color: baseTheme.light.colors.primary700,
      backgroundColor: baseTheme.light.colors.primary50,
      border: `1px solid ${baseTheme.light.colors.primary50}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`
    },
    hover: {
      color: baseTheme.light.colors.primary700,
      border: `1px solid ${baseTheme.light.colors.primary50}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      backgroundColor: baseTheme.light.colors.primary100
    },
    active: {
      color: baseTheme.light.colors.primary700,
      backgroundColor: baseTheme.light.colors.primary50,
      border: `1px solid ${baseTheme.light.colors.primary50}`,
      boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${baseTheme.light.colors.primary100}`
    },
    disable: {
      border: `1px solid ${baseTheme.light.colors.primary50}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      color: baseTheme.light.colors.primary300,
      backgroundColor: baseTheme.light.colors.primary25
    }
  },
  tertiary: {
    normal: {
      color: baseTheme.light.colors.gray700,
      backgroundColor: baseTheme.light.colors.white,
      border: `1px solid ${baseTheme.light.colors.gray200}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`
    },
    hover: {
      border: `1px solid ${baseTheme.light.colors.gray200}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      color: baseTheme.light.colors.gray800,
      backgroundColor: baseTheme.light.colors.gray50
    },
    active: {
      color: baseTheme.light.colors.gray700,
      backgroundColor: baseTheme.light.colors.white,
      border: `1px solid ${baseTheme.light.colors.primary50}`,
      boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${baseTheme.light.colors.gray100}`
    },
    disable: {
      border: `1px solid ${baseTheme.light.colors.gray200}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      color: baseTheme.light.colors.gray300,
      backgroundColor: baseTheme.light.colors.white
    }
  },
  link: {
    normal: {
      padding: 0,
      height: 'auto',
      color: baseTheme.light.colors.primary600
    },
    hover: {
      color: baseTheme.light.colors.primary800
    },
    active: {
      color: baseTheme.light.colors.primary700
    },
    disable: {
      color: baseTheme.light.colors.gray300
    }
  },
  danger: {
    normal: {
      color: baseTheme.light.colors.white,
      backgroundColor: baseTheme.light.colors.error600,
      border: `1px solid ${baseTheme.light.colors.error600}`,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`
    },
    hover: {
      color: baseTheme.light.colors.white,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      backgroundColor: baseTheme.light.colors.error700,
      border: `1px solid ${baseTheme.light.colors.error700}`
    },
    active: {
      color: baseTheme.light.colors.white,
      backgroundColor: baseTheme.light.colors.error600,
      border: `1px solid ${baseTheme.light.colors.error600}`,
      boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${baseTheme.light.colors.error100}`
    },
    disable: {
      color: baseTheme.light.colors.white,
      boxShadow: `0px 1px 2px 0px ${boxShadowColor}`,
      backgroundColor: baseTheme.light.colors.error200,
      border: `1px solid ${baseTheme.light.colors.error200}`
    }
  },
  unstyled: {
    normal: {},
    hover: {},
    active: {},
    disable: {}
  }
};
export const getButtonStateStyles = (variant: VariantTypes, customStateStyles?: Partial<ButtonStateStyles>): ButtonStateStyles => {
  const specificVariant = defaultButtonVariantsStyles[variant];
  return {
    normal: {
      ...specificVariant.normal,
      ...customStateStyles?.normal
    },
    hover: {
      ...specificVariant.hover,
      ...customStateStyles?.hover
    },
    active: {
      ...specificVariant.active,
      ...customStateStyles?.active
    },
    disable: {
      ...specificVariant.disable,
      ...customStateStyles?.disable
    }
  };
};
export const StyledButton = /*#__PURE__*/styled(ReakitButton)({
  name: "StyledButton",
  class: "omponents__ui_library__Button__Button_styles_tsx-StyledButton-s927fbq",
  vars: {
    "s927fbq-0": [({
      variant
    }) => variant === 'link' ? '0' : '0 0.8rem'],
    "s927fbq-1": [({
      variant
    }) => variant === 'link' ? 'auto' : '2.125rem'],
    "s927fbq-2": [({
      variant
    }) => variant === 'link' ? '0' : ' 0 1rem'],
    "s927fbq-3": [({
      variant
    }) => variant === 'link' ? 'auto' : '2.5rem'],
    "s927fbq-4": [({
      variant
    }) => variant === 'link' ? '0' : '0 1.1rem'],
    "s927fbq-5": [({
      variant
    }) => variant === 'link' ? '0' : '0 0.6rem'],
    "s927fbq-6": [({
      variant
    }) => variant === 'link' ? '0' : '0 0.7rem'],
    "s927fbq-7": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).normal.color as string)],
    "s927fbq-8": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).normal.backgroundColor as string)],
    "s927fbq-9": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).normal.border as string)],
    "s927fbq-10": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).normal.boxShadow as string)],
    "s927fbq-11": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).hover.color as string)],
    "s927fbq-12": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).hover.backgroundColor as string)],
    "s927fbq-13": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).hover.border as string)],
    "s927fbq-14": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).hover.boxShadow as string)],
    "s927fbq-15": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).active.color as string)],
    "s927fbq-16": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).active.backgroundColor as string)],
    "s927fbq-17": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).active.border as string)],
    "s927fbq-18": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).active.boxShadow as string)],
    "s927fbq-19": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).disable.color as string)],
    "s927fbq-20": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).disable.backgroundColor as string)],
    "s927fbq-21": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).disable.border as string)],
    "s927fbq-22": [({
      variant,
      customStateStyles
    }) => (getButtonStateStyles(variant, customStateStyles).disable.boxShadow as string)]
  }
});

require("./Button.styles.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./Button.styles.tsx");